import { useEffect, useRef } from "react";
import {
  ACESFilmicToneMapping, AdditiveBlending, AmbientLight, BoxGeometry, BufferGeometry, CircleGeometry, Color,
  ConeGeometry, DirectionalLight, DoubleSide, Float32BufferAttribute, FogExp2, Group, LineBasicMaterial,
  LinearFilter, Line, LineSegments, Mesh, MeshBasicMaterial, MeshPhysicalMaterial, OrthographicCamera,
  PerspectiveCamera, PlaneGeometry, PointLight, Points, PointsMaterial, RingGeometry, Scene, ShaderMaterial,
  SRGBColorSpace, Texture, TextureLoader, TorusGeometry, Vector2, WebGLRenderer,
} from "three";
import styles from "./TbltopTitleScreen.module.css";
import { mediaUrl } from "../lib/media-url";

export const TITLE_BACKGROUND_URL = mediaUrl("/assets/title/tbltop-main-menu-background.webp");
const PARTICLE_COUNT = 620;
const SWIRL_RIBBON_COUNT = 9;

const BACKGROUND_VERTEX_SHADER = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const BACKGROUND_FRAGMENT_SHADER = `
  precision highp float;
  uniform sampler2D uMap;
  uniform float uLoaded;
  uniform float uTime;
  uniform vec2 uCoverScale;
  uniform vec2 uPointer;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float spiral(vec2 point, vec2 center, float arms, float curl, float phase, float radius) {
    vec2 delta = point - center;
    delta.x *= uResolution.x / max(uResolution.y, 1.0);
    float distanceFromCenter = length(delta);
    float angle = atan(delta.y, delta.x);
    float ribbon = pow(0.5 + 0.5 * cos(angle * arms - distanceFromCenter * curl + phase), 20.0);
    float envelope = smoothstep(0.025, 0.12, distanceFromCenter) * (1.0 - smoothstep(radius * 0.45, radius, distanceFromCenter));
    return ribbon * envelope;
  }

  void main() {
    vec2 imageUv = (vUv - 0.5) * uCoverScale + 0.5;
    imageUv += uPointer * vec2(0.008, -0.006);
    vec3 source = texture2D(uMap, clamp(imageUv, 0.001, 0.999)).rgb;
    source = mix(vec3(0.035, 0.055, 0.045), source, uLoaded);

    float luminance = dot(source, vec3(0.299, 0.587, 0.114));
    vec3 color = mix(vec3(luminance), source, 0.78);
    color = mix(color, color * vec3(0.72, 0.86, 0.78), 0.2);
    float edge = 1.0 - smoothstep(0.25, 0.78, length((vUv - 0.5) * vec2(1.05, 0.94)));
    float sideShade = 1.0 - 0.22 * (1.0 - smoothstep(0.08, 0.36, vUv.x)) - 0.25 * smoothstep(0.68, 0.94, vUv.x);
    color *= mix(0.38, 0.72, edge) * sideShade;

    float gold = spiral(vUv, vec2(0.53, 0.52), 3.0, 31.0, uTime * 0.22, 1.02);
    gold += 0.7 * spiral(vUv, vec2(0.42, 0.66), 2.0, -35.0, -uTime * 0.17, 0.72);
    float teal = spiral(vUv, vec2(0.67, 0.43), 4.0, -39.0, uTime * 0.16 + 1.7, 0.86);
    teal += 0.55 * spiral(vUv, vec2(0.31, 0.35), 3.0, 43.0, -uTime * 0.13, 0.65);
    float flowingMist = 0.5 + 0.5 * sin(vUv.x * 19.0 + sin(vUv.y * 13.0 - uTime * 0.14) * 1.8 + uTime * 0.1);
    color += vec3(0.92, 0.54, 0.14) * gold * (0.105 + flowingMist * 0.055);
    color += vec3(0.18, 0.67, 0.63) * teal * (0.07 + (1.0 - flowingMist) * 0.04);
    color += vec3(0.11, 0.16, 0.13) * flowingMist * 0.035;
    gl_FragColor = vec4(color, 1.0);
  }
`;

function seededRandom(seed = 0x4a465454) {
  return () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x1_0000_0000;
  };
}

export function TitleScreenWebGL() {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const host = hostRef.current; const canvas = canvasRef.current;
    if (!host || !canvas) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let renderer: WebGLRenderer;
    try {
      renderer = new WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
    } catch {
      host.dataset.renderer = "css-fallback";
      return;
    }

    renderer.setClearColor(0x07100d, 0);
    renderer.setPixelRatio(Math.min(1.6, window.devicePixelRatio || 1));
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.28;
    renderer.autoClear = false;
    host.dataset.renderer = "webgl";
    host.dataset.particleCount = String(PARTICLE_COUNT);
    host.dataset.swirlCount = String(SWIRL_RIBBON_COUNT + 4);
    host.dataset.backgroundSource = TITLE_BACKGROUND_URL;

    const backgroundScene = new Scene();
    const backgroundCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const backgroundGeometry = new PlaneGeometry(2, 2);
    const backgroundMaterial = new ShaderMaterial({
      uniforms: {
        uMap: { value: null }, uLoaded: { value: 0 }, uTime: { value: 0 },
        uCoverScale: { value: new Vector2(1, 1) }, uPointer: { value: new Vector2() }, uResolution: { value: new Vector2(1, 1) },
      },
      vertexShader: BACKGROUND_VERTEX_SHADER, fragmentShader: BACKGROUND_FRAGMENT_SHADER,
      depthTest: false, depthWrite: false,
    });
    backgroundScene.add(new Mesh(backgroundGeometry, backgroundMaterial));
    let backgroundTexture: Texture | undefined; let disposed = false;
    new TextureLoader().load(TITLE_BACKGROUND_URL, (texture) => {
      if (disposed) { texture.dispose(); return; }
      texture.colorSpace = SRGBColorSpace; texture.minFilter = LinearFilter; texture.magFilter = LinearFilter; texture.generateMipmaps = false;
      backgroundTexture = texture; backgroundMaterial.uniforms.uMap.value = texture; backgroundMaterial.uniforms.uLoaded.value = 1;
      host.dataset.backgroundTexture = "loaded";
      if (reducedMotion) requestAnimationFrame(() => {
        if (disposed) return;
        renderer.clear(); renderer.render(backgroundScene, backgroundCamera); renderer.clearDepth(); renderer.render(scene, camera);
      });
    }, undefined, () => { if (!disposed) host.dataset.backgroundTexture = "fallback"; });

    const scene = new Scene();
    scene.fog = new FogExp2(0x07100d, 0.035);
    const camera = new PerspectiveCamera(43, 1, 0.1, 80);
    camera.position.set(0, 8.7, 13.5);
    camera.lookAt(0, 0, 0);

    scene.add(new AmbientLight(0x6f845f, 0.72));
    const moonLight = new DirectionalLight(0x90aeb0, 2.2);
    moonLight.position.set(-7, 12, 2);
    scene.add(moonLight);
    const fireLight = new PointLight(0xf2a33c, 45, 21, 1.85);
    fireLight.position.set(4, 2.5, 1);
    scene.add(fireLight);

    const tableMaterial = new MeshPhysicalMaterial({ color: 0x102019, roughness: 0.88, metalness: 0.06, transparent: true, opacity: 0.12, clearcoat: 0.08, depthWrite: false });
    const table = new Mesh(new PlaneGeometry(25, 17, 1, 1), tableMaterial);
    table.rotation.x = -Math.PI / 2;
    table.position.y = -0.32;
    scene.add(table);

    const random = seededRandom();
    const gridVertices: number[] = [];
    for (let x = -10; x <= 10; x += 1) gridVertices.push(x, -0.25, -7, x, -0.25, 7);
    for (let z = -7; z <= 7; z += 1) gridVertices.push(-10, -0.25, z, 10, -0.25, z);
    const gridGeometry = new BufferGeometry();
    gridGeometry.setAttribute("position", new Float32BufferAttribute(gridVertices, 3));
    const grid = new LineSegments(gridGeometry, new LineBasicMaterial({ color: 0x9b8652, transparent: true, opacity: 0.12 }));
    scene.add(grid);

    const swirlRibbons = new Group();
    swirlRibbons.position.set(0.7, -0.11, -0.4);
    for (let ribbonIndex = 0; ribbonIndex < SWIRL_RIBBON_COUNT; ribbonIndex += 1) {
      const vertices: number[] = [];
      const ribbonOffset = ribbonIndex * Math.PI * 2 / SWIRL_RIBBON_COUNT;
      for (let step = 0; step < 170; step += 1) {
        const progress = step / 169;
        const radius = 0.45 + progress * (5.2 + ribbonIndex * 0.11);
        const angle = ribbonOffset + progress * Math.PI * (3.45 + ribbonIndex * 0.025);
        vertices.push(Math.cos(angle) * radius, 0.025 + ribbonIndex * 0.008, Math.sin(angle) * radius * 0.79);
      }
      const geometry = new BufferGeometry(); geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
      const material = new LineBasicMaterial({ color: ribbonIndex % 3 === 1 ? 0x5aa9a0 : 0xd3a454, transparent: true, opacity: ribbonIndex % 3 === 1 ? 0.18 : 0.14, blending: AdditiveBlending, depthWrite: false });
      swirlRibbons.add(new Line(geometry, material));
    }
    scene.add(swirlRibbons);

    const astrolabe = new Group();
    astrolabe.position.set(0.7, -0.18, -0.4);
    scene.add(astrolabe);
    const ringColors = [0xe2af4b, 0x6fa89d, 0xd7c286, 0xe2af4b];
    [2.1, 3.25, 4.55, 6.2].forEach((radius, index) => {
      const ring = new Mesh(
        new RingGeometry(radius - 0.025 - index * 0.01, radius + 0.025 + index * 0.01, 128),
        new MeshBasicMaterial({ color: ringColors[index], transparent: true, opacity: 0.32 - index * 0.045, side: DoubleSide, blending: AdditiveBlending, depthWrite: false }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = index * 0.018;
      astrolabe.add(ring);
    });
    for (let index = 0; index < 16; index += 1) {
      const spoke = new Mesh(
        new BoxGeometry(index % 4 === 0 ? 0.055 : 0.025, 0.018, index % 4 === 0 ? 5.7 : 4.2),
        new MeshBasicMaterial({ color: index % 4 === 0 ? 0xd6ae62 : 0x78948c, transparent: true, opacity: index % 4 === 0 ? 0.34 : 0.18 }),
      );
      spoke.rotation.y = index * Math.PI / 8;
      spoke.position.y = 0.045;
      astrolabe.add(spoke);
    }
    const core = new Mesh(
      new CircleGeometry(0.62, 48),
      new MeshBasicMaterial({ color: 0xf2b84a, transparent: true, opacity: 0.28, blending: AdditiveBlending, depthWrite: false }),
    );
    core.rotation.x = -Math.PI / 2;
    core.position.y = 0.09;
    astrolabe.add(core);

    const beaconMaterial = new MeshPhysicalMaterial({ color: 0x34210f, emissive: new Color(0xe59727), emissiveIntensity: 2.4, roughness: 0.34, metalness: 0.72 });
    const beacons: Group[] = [];
    [[-6.6, -3.6], [6.8, 3.3], [6.3, -4.2], [-5.4, 4.5]].forEach(([x, z], index) => {
      const beacon = new Group();
      const pedestal = new Mesh(new TorusGeometry(0.34, 0.07, 10, 32), beaconMaterial);
      pedestal.rotation.x = Math.PI / 2;
      beacon.add(pedestal);
      const flame = new Mesh(new ConeGeometry(0.13, 0.68, 12), beaconMaterial);
      flame.position.y = 0.38;
      beacon.add(flame);
      const glow = new PointLight(index % 2 ? 0x73bcb5 : 0xf0a231, 13, 4.6, 2);
      glow.position.y = 0.6;
      beacon.add(glow);
      beacon.position.set(x, -0.18, z);
      scene.add(beacon);
      beacons.push(beacon);
    });

    const particlePositions = new Float32Array(PARTICLE_COUNT * 3);
    const particleSpeeds = new Float32Array(PARTICLE_COUNT);
    for (let index = 0; index < PARTICLE_COUNT; index += 1) {
      const offset = index * 3;
      particlePositions[offset] = (random() - 0.5) * 25;
      particlePositions[offset + 1] = random() * 7 - 0.1;
      particlePositions[offset + 2] = (random() - 0.5) * 18;
      particleSpeeds[index] = 0.12 + random() * 0.24;
    }
    const particleGeometry = new BufferGeometry();
    particleGeometry.setAttribute("position", new Float32BufferAttribute(particlePositions, 3));
    const particles = new Points(particleGeometry, new PointsMaterial({ color: 0xe5b95f, size: 0.055, transparent: true, opacity: 0.52, blending: AdditiveBlending, depthWrite: false, sizeAttenuation: true }));
    scene.add(particles);

    let width = 1; let height = 1;
    const resize = () => {
      width = Math.max(1, host.clientWidth); height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height; camera.updateProjectionMatrix();
      const viewportAspect = width / height; const imageAspect = 16 / 9;
      backgroundMaterial.uniforms.uCoverScale.value.set(viewportAspect > imageAspect ? 1 : viewportAspect / imageAspect, viewportAspect > imageAspect ? imageAspect / viewportAspect : 1);
      backgroundMaterial.uniforms.uResolution.value.set(width, height);
    };
    const observer = new ResizeObserver(resize); observer.observe(host); resize();
    let pointerX = 0; let pointerY = 0;
    const trackPointer = (event: PointerEvent) => {
      pointerX = (event.clientX / Math.max(window.innerWidth, 1) - 0.5) * 2;
      pointerY = (event.clientY / Math.max(window.innerHeight, 1) - 0.5) * 2;
    };
    if (!reducedMotion) window.addEventListener("pointermove", trackPointer, { passive: true });

    let frame = 0; const startedAt = performance.now();
    const render = (now: number) => {
      const time = (now - startedAt) / 1000;
      if (!document.hidden) {
        astrolabe.rotation.y = time * 0.025;
        swirlRibbons.rotation.y = time * 0.035;
        core.scale.setScalar(0.93 + Math.sin(time * 1.35) * 0.1);
        core.material.opacity = 0.23 + Math.sin(time * 1.35) * 0.06;
        beacons.forEach((beacon, index) => { beacon.scale.y = 0.92 + Math.sin(time * (1.7 + index * 0.11) + index) * 0.13; });
        const positions = particleGeometry.attributes.position.array as Float32Array;
        for (let index = 0; index < PARTICLE_COUNT; index += 1) {
          const y = index * 3 + 1;
          positions[y] += particleSpeeds[index] * (reducedMotion ? 0 : 0.012);
          if (positions[y] > 7) positions[y] = 0;
        }
        particleGeometry.attributes.position.needsUpdate = true;
        if (!reducedMotion) {
          camera.position.x += (pointerX * 0.55 - camera.position.x) * 0.018;
          camera.position.y += (8.7 - pointerY * 0.25 - camera.position.y) * 0.018;
          camera.lookAt(0, 0, 0);
        }
        fireLight.intensity = 42 + Math.sin(time * 2.1) * 5 + Math.sin(time * 4.7) * 2;
        backgroundMaterial.uniforms.uTime.value = reducedMotion ? 0 : time;
        backgroundMaterial.uniforms.uPointer.value.x += (pointerX - backgroundMaterial.uniforms.uPointer.value.x) * 0.025;
        backgroundMaterial.uniforms.uPointer.value.y += (pointerY - backgroundMaterial.uniforms.uPointer.value.y) * 0.025;
        renderer.clear();
        renderer.render(backgroundScene, backgroundCamera);
        renderer.clearDepth();
        renderer.render(scene, camera);
        if (host.dataset.renderedFrame !== "true") host.dataset.renderedFrame = "true";
      }
      if (!reducedMotion) frame = requestAnimationFrame(render);
    };
    render(performance.now());

    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect(); window.removeEventListener("pointermove", trackPointer);
      const geometries = new Set<BufferGeometry>(); const materials = new Set<MeshBasicMaterial | MeshPhysicalMaterial | PointsMaterial | LineBasicMaterial>();
      scene.traverse((object) => {
        if (object instanceof Mesh || object instanceof Points || object instanceof Line) {
          geometries.add(object.geometry);
          const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
          objectMaterials.forEach((material) => materials.add(material as MeshBasicMaterial));
        }
      });
      geometries.forEach((geometry) => geometry.dispose()); materials.forEach((material) => material.dispose());
      backgroundTexture?.dispose(); backgroundGeometry.dispose(); backgroundMaterial.dispose();
      renderer.renderLists.dispose(); renderer.dispose();
    };
  }, []);

  return <div ref={hostRef} className={styles.webgl} data-title-screen-webgl="war-table" aria-hidden="true">
    <canvas ref={canvasRef} />
  </div>;
}
